import { useQuery } from "@tanstack/react-query";
import * as BrmGql from "generated/graphql";
import * as Brm from "services/brm";
import { QueryOption } from "libs/react-query";
import * as KeyFactory from "brm/tables/details/api/element-query-key-factory";
import { BrmError } from "../../../errors/BrmError";
import { DetailTableType } from "../../../types/DetailTableType";

const getElementInData = (
  elementId: string,
  elementType: string,
  nodeInData: BrmGql.GetNodeInDataQuery | undefined
) => {
  const inDataMap: IInDataMap = {
    [DetailTableType.NODES.key]: () => nodeInData?.node?.inData,
    [DetailTableType.ACTIVITIES.key]: (id) => Brm.activityApi.getActivityInData(id),
  };

  if (inDataMap[elementType]) {
    return inDataMap[elementType](elementId) || [];
  }
  throw new BrmError(`Unsupported element type: ${elementType}`);
};

interface IElementInDataProps {
  elementId: string;
  elementName: string;
  projectId: string;
  variantId: string;
  options: QueryOption<typeof getElementInData>;
}

interface IInDataMap {
  [index: string]: (id?: string) => any;
}

/**
 * custom-hook to retreive all inData
 *
 * @param {string} elementId - uuid of the selected element
 * @param {string} elementName - type/category of the selected element
 * @param {string} projectId - uuid of the project
 * @param {string} variantId - uuid of the variant
 * @param {object} options - configuration for the react-query
 */
export const useElementInData = ({
  elementId,
  elementName,
  projectId,
  variantId,
  options = {},
}: IElementInDataProps) => {
  const nodeInDataQuery = BrmGql.useGetNodeInDataQuery(
    { project: projectId, variant: variantId, id: elementId },
    { enabled: !!elementId && !!projectId && !!variantId && elementName === DetailTableType.NODES.key }
  );

  const elementInDataQuery = useQuery({
    ...options,
    queryKey: KeyFactory.elementKeys.inData(elementId, elementName, nodeInDataQuery.data),
    queryFn: () => getElementInData(elementId, elementName, nodeInDataQuery.data),
  });

  if (nodeInDataQuery.isError) {
    return nodeInDataQuery;
  }

  return elementInDataQuery;
};
